import {
  autoUpdate,
  flip,
  FloatingOverlay,
  FloatingPortal,
  offset,
  Placement,
  ReferenceType,
  shift,
  useFloating,
} from "@floating-ui/react";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import styled from "styled-components";
import {
  CreateTransient,
  LiElementProps,
  UlElementProps,
} from "../../shared/types";
import { Button, ButtonProps } from "../button";
import { Icon } from "../icons";

type MenuContextType = {
  isOpen: boolean;
  setIsOpen: Dispatch<React.SetStateAction<boolean>>;
  triggerWidth: number;
  setTriggerWidth: Dispatch<React.SetStateAction<number>>;
  floatingStyles?: React.CSSProperties;
  setReference: (node: ReferenceType | null) => void;
  setFloating: (node: HTMLElement | null) => void;
};
const MenuContext = createContext<MenuContextType>({} as MenuContextType);

type MenuProps = PropsWithChildren & {
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  itemsPanelPlacement?: Placement;
};
export const Menu = (props: MenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const { children, ...rest } = props;
  const { refs, elements, floatingStyles } = useFloating({
    placement: props.itemsPanelPlacement ?? "bottom-start",
    open: isOpen,
    onOpenChange: (open) => {
      if (open) rest.onMenuOpen?.();
      else props.onMenuClose?.();
    },
    // ensures that position is updated if the size of the menu changes.
    whileElementsMounted: autoUpdate,
    middleware: [
      // changes the placement of the floating element to keep it in view vertically.
      flip(),
      // changes the placement of the floating element to keep it in view horizontally.
      shift(),
      offset({ mainAxis: -10 }),
    ],
  });

  useLayoutEffect(() => {
    if (elements.reference) {
      setTriggerWidth(elements.reference.getBoundingClientRect().width);
    }
  }, [elements.reference]);

  return (
    <MenuContext.Provider
      value={{
        isOpen,
        setIsOpen,
        triggerWidth,
        setTriggerWidth,
        floatingStyles,
        setReference: refs.setReference,
        setFloating: refs.setFloating,
      }}
    >
      <Container {...rest}>{children}</Container>
    </MenuContext.Provider>
  );
};

type MenuTriggerProps = PropsWithChildren<ButtonProps> & {
  withExpandIcon?: boolean;
  prefixIcon?: ReactNode;
  showClear?: boolean;
  onClear?: () => void;
};

const MenuTrigger = ({
  children,
  withExpandIcon,
  prefixIcon,
  showClear,
  onClear,
  ...rest
}: MenuTriggerProps) => {
  const { isOpen, setIsOpen, setReference } = useContext(MenuContext);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <Button
      ref={setReference}
      {...rest}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {prefixIcon}
      {children}
      {showClear && (
        <ClearButton
          onClick={handleClear}
          type="button"
          data-testid="deselect-button"
        >
          <Icon.Close />
        </ClearButton>
      )}
      {withExpandIcon && <TriggerExpandIcon $isOpen={isOpen} />}
    </Button>
  );
};

Menu.Trigger = MenuTrigger;

const MenuItems = ({ children, ...rest }: UlElementProps) => {
  const { isOpen, triggerWidth, setIsOpen, setFloating, floatingStyles } =
    useContext(MenuContext);

  return isOpen ? (
    <>
      <FloatingOverlay
        onClick={() => {
          setIsOpen(false);
        }}
      />
      <FloatingPortal>
        <ItemsPanel
          ref={setFloating}
          style={floatingStyles}
          $triggerWidth={triggerWidth}
          {...rest}
        >
          {children}
        </ItemsPanel>
      </FloatingPortal>
    </>
  ) : null;
};

Menu.Items = MenuItems;

type MenuItemProps = LiElementProps & {
  onClick?: () => void;
  selected?: boolean;
};

const MenuItem = ({ children, onClick, selected, ...rest }: MenuItemProps) => {
  const { setIsOpen } = useContext(MenuContext);

  return (
    <Item
      $selected={selected}
      onClick={() => {
        setIsOpen(false);
        onClick?.();
      }}
      {...rest}
    >
      {children}
    </Item>
  );
};

Menu.Item = MenuItem;

const Container = styled.div`
  position: relative;
`;

interface ItemsPanelProps {
  triggerWidth?: number;
}

const ItemsPanel = styled.ul<CreateTransient<ItemsPanelProps>>`
  // when used inside a MUI modal, that modal has z-index: 1300, so we need to be above that.
  z-index: 1500;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  /* margin: ${({ theme }) => theme.MARGIN.SM}; */
  padding: 0;
  list-style: none;
  box-shadow: 0px 2px 8px rgba(29, 27, 27, 0.2);
  min-width: ${({ $triggerWidth }) =>
    $triggerWidth ? `${$triggerWidth}px` : "auto"};
`;

interface ItemProps {
  selected?: boolean;
}

const Item = styled.li<CreateTransient<ItemProps>>`
  font-family: ${({ theme }) => theme.FONT.FAMILY.BODY};
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};
  font-weight: ${({ theme, $selected }) =>
    $selected ? theme.FONT.WEIGHT.SEMIBOLD : theme.FONT.WEIGHT.REGULAR};
  padding: 8px 16px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.COLORS.BLACK_GREY_OPACITY};
    text-transform: underline;
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

const TriggerExpandIcon = styled(Icon.Expand)<
  CreateTransient<{ isOpen: boolean }>
>`
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease-in-out;
`;
